import { reducedMotion$ } from './reduced-motion'

/**
 * The one insertion line every drag surface in the library paints. It lives in
 * `document.body` rather than in the dragging list's own root because the line
 * is viewport-positioned and must outrank whatever `overflow: hidden` the list
 * scrolls inside — a line clipped by its own list marks a drop the user cannot
 * see.
 *
 * Reduced motion drops the *tween* between positions, never the line: a user
 * who has asked for less motion still has to be told where the item lands.
 */

export type DropSide = 'before' | 'after'

/**
 * Which way the order runs through the list the target belongs to. The line is
 * drawn ACROSS that direction — a list running `down` is marked by a horizontal
 * rule at the target's top or bottom edge, one running `across` by a vertical
 * rule at its leading or trailing edge — because a rule drawn along the order
 * instead of across it points at a gap the drop will not use, and in a wrapping
 * grid it points at the neighbouring ROW while the gesture named a neighbouring
 * COLUMN.
 *
 * A wrapping grid is `across`: its order is one sequence read row by row, so
 * every tile answers "before or after me" on the inline axis, whichever row it
 * happens to sit in.
 */
export type DropAxis = 'down' | 'across'

let line: HTMLElement | null = null
let caps: HTMLElement[] = []

function ensureLine(): HTMLElement {
	// Connected, not merely created: the cached node is the only one anything
	// paints onto, so once something detaches it — a harness clearing the body, a
	// framework re-rendering it — every later paint lands on a node nobody can
	// see, and the indicator stops appearing with no error anywhere.
	if (line?.isConnected) return line

	const el = document.createElement('div')
	el.setAttribute('data-schmancy-drop-line', '')
	Object.assign(el.style, {
		position: 'fixed',
		backgroundColor: 'var(--schmancy-sys-color-primary-default, #6750A4)',
		borderRadius: '1px',
		pointerEvents: 'none',
		zIndex: '10000',
		boxShadow: '0 0 4px var(--schmancy-sys-color-primary-default, #6750A4)',
		display: 'none',
	})

	caps = ['leading', 'trailing'].map(() => {
		const cap = document.createElement('div')
		Object.assign(cap.style, {
			position: 'absolute',
			width: '6px',
			height: '6px',
			borderRadius: '50%',
			backgroundColor: 'var(--schmancy-sys-color-primary-default, #6750A4)',
		})
		el.appendChild(cap)
		return cap
	})

	document.body.appendChild(el)
	line = el
	return el
}

/** Paint the line at the leading or trailing edge of `target`, across the order's own direction. */
export function showDropIndicator(target: HTMLElement, side: DropSide, axis: DropAxis = 'down'): void {
	const el = ensureLine()
	const rect = target.getBoundingClientRect()

	el.style.transition = reducedMotion$.value
		? 'none'
		: 'top 100ms ease, left 100ms ease, width 100ms ease, height 100ms ease'

	// Every one of the four is written on every paint: the line is a singleton and
	// keeps whatever the previous axis left behind otherwise.
	Object.assign(
		el.style,
		axis === 'across'
			? {
					top: `${rect.top}px`,
					left: `${side === 'before' ? rect.left - 1 : rect.right + 1}px`,
					width: '2px',
					height: `${rect.height}px`,
				}
			: {
					top: `${side === 'before' ? rect.top - 1 : rect.bottom + 1}px`,
					left: `${rect.left}px`,
					width: `${rect.width}px`,
					height: '2px',
				},
	)
	el.style.display = 'block'

	// The caps mark the rule's two ENDS, which swap axis with the rule itself.
	const [leading, trailing] = caps
	if (leading && trailing) {
		Object.assign(
			leading.style,
			axis === 'across'
				? { left: '-2px', top: '-3px', right: '', bottom: '' }
				: { top: '-2px', left: '-3px', right: '', bottom: '' },
		)
		Object.assign(
			trailing.style,
			axis === 'across'
				? { left: '-2px', bottom: '-3px', right: '', top: '' }
				: { top: '-2px', right: '-3px', left: '', bottom: '' },
		)
	}
}

export function hideDropIndicator(): void {
	if (line) line.style.display = 'none'
}

/**
 * The drop ZONE outline — the sibling affordance to the line above. The line
 * says *where in the list*; the outline says *which container*, and the two
 * paint together because both are facts the user is choosing. The treatment is
 * the Kevin inspector's own component outline (`kevin-pick-overlay.view.ts`):
 * a hairline dashed primary rule at 30% alpha, so it reads as a boundary being
 * offered rather than a border the container actually has.
 *
 * It sits one layer BELOW the line: a zone outline that covered the insertion
 * line would hide the more precise of the two answers.
 */

let zoneBox: HTMLElement | null = null

function ensureZoneBox(): HTMLElement {
	if (zoneBox?.isConnected) return zoneBox

	const el = document.createElement('div')
	el.setAttribute('data-schmancy-drop-zone', '')
	Object.assign(el.style, {
		position: 'fixed',
		border: '1px dashed color-mix(in srgb, var(--schmancy-sys-color-primary-default, #6750A4) 30%, transparent)',
		borderRadius: '12px',
		// The rect written below is the zone's own, so the border must grow
		// inward — an outline drawn 1px outside it would sit over its neighbour.
		boxSizing: 'border-box',
		pointerEvents: 'none',
		zIndex: '9999',
		display: 'none',
	})

	document.body.appendChild(el)
	zoneBox = el
	return el
}

/** Outline `zone`'s whole box at its viewport rect. */
export function showDropZone(zone: HTMLElement): void {
	const el = ensureZoneBox()
	const rect = zone.getBoundingClientRect()

	el.style.transition = reducedMotion$.value ? 'none' : 'all 75ms'
	Object.assign(el.style, {
		top: `${rect.top}px`,
		left: `${rect.left}px`,
		width: `${rect.width}px`,
		height: `${rect.height}px`,
		display: 'block',
	})
}

export function hideDropZone(): void {
	if (zoneBox) zoneBox.style.display = 'none'
}
